import React, { useEffect, useState } from "react";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
    getSortedRowModel,
    getExpandedRowModel,
} from "@tanstack/react-table";

import "./styles.scss";

export default function Table(props) {
    const columnHelper = createColumnHelper();
    const [data, setData] = useState(props.data ?? []);
    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: props.page ?? 0,
        pageSize: props.pageSize ?? 10
    });
    const [sorting, setSorting] = useState([]);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (props.manual && props.onFetchData !== null && initialized) {
            if (pageIndex === 0 && data.length === 0)
                return;

            var pagination = {
                page: pageIndex,
                pageSize: pageSize,
                sorted: sorting
            }

            props.onFetchData(pagination).then(response => {
                setData(response.data);
            })
        }

        setInitialized(true);

    }, [pageIndex, pageSize, sorting]);

    const table = useReactTable({
        ...props,
        data,
        columns: props.columns.map((c) => {
            return columnHelper.accessor(c.id !== null ? c.id : c.accessor, {
                header: c.Header,
                cell: typeof c.accessor === "function" ? c.accessor : (d) => d[c.accessor],
                accessor:
                    typeof c.accessor === "function" ? c.accessor : (d) => d[c.accessor],
                className: c.className,
                headerClassName: c.headerClassName,
                size: c.width ?? undefined,
                maxSize: c.maxWidth ?? undefined,
                minSize: c.minWidth ?? undefined
            });
        }),
        columnResizeMode: "onChange",
        pageCount: props.pages,
        manualPagination: props.manual,
        state: {
            sorting: sorting,
            pagination: {
                pageIndex,
                pageSize
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        getExpandedRowModel: getExpandedRowModel(),
    })

    function isSorted(h) {
        if (h.column.getIsSorted() == "asc")
            return '-sort-asc';
        else if (h.column.getIsSorted() == "desc")
            return '-sort-desc';
        else return '';
    }

    return (
        <>
            {
                props.hasTitle &&
                <>
                    <div className="col-12 card-table-title px-3">
                        <h3>
                            Resultado da busca
                        </h3>
                    </div>
                </>
            }
            <div className={"ReactTable " + props.className}>
                <table
                    className="rt-table"
                    role="grid"
                >
                    <thead className="rt-thead -header"
                        style={{ minWidth: '900px', width: '100%' }}
                    >
                        {table.getHeaderGroups().map((hg) => (
                            <tr className="rt-tr" key={hg.id}
                                role="row"
                            >
                                {hg.headers.map((h) => (
                                    <th
                                        key={h.id}
                                        className={`rt-th rt-resizable-header ${(isSorted(h))} -cursor-pointer fw-light ${h.column.columnDef.headerClassName}`}
                                        role="columnheader"
                                        tabIndex={-1}
                                        colSpan={h.colSpan}
                                        style={{
                                            width: h.getSize(),
                                            flex: '100 0 auto'
                                        }}
                                    >
                                        <div className="rt-resizable-header-content"
                                            {...{
                                                className: h.column.getCanSort() ? 'cursor-pointer select-none' : '',
                                                onClick: h.column.getToggleSortingHandler(),
                                            }}>

                                            {
                                                flexRender(h.column.columnDef.header, h.getContext())
                                            }
                                        </div>

                                        <div
                                            className={`rt-resizer ${h.column.getIsResizing() ? "rt-resizing" : ""
                                                }`}
                                            onMouseDown={h.getResizeHandler()}
                                            onTouchStart={h.getResizeHandler()}
                                        ></div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="rt-tbody"
                        style={{ minWidth: '900px', width: '100%' }}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <React.Fragment key={row.id}>
                                <tr
                                    className={`rt-tr ${row.id % 2 !== 0 ? "-odd" : ""}`}
                                    role="row"
                                    key={row.id}
                                >
                                    {
                                        row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className={`rt-td ${cell.column.columnDef.className}`}
                                                role="gridcell"
                                                style={{
                                                    flex: '100 0 auto',
                                                    width: cell.column.getSize()
                                                }}
                                            >
                                                {
                                                    cell.column.columnDef.accessorKey == "expander" ?
                                                        flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        ) :
                                                        flexRender(
                                                            cell.column.columnDef.cell,
                                                            row.original
                                                        )
                                                }
                                            </td>
                                        ))
                                    }
                                </tr>
                                {row.getIsExpanded() && (
                                    <tr>
                                        <td colSpan={row.getVisibleCells().length}>
                                            {props.renderSubComponent(row)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                <div className="pagination-bottom">
                    <div className="-pagination">
                        <div className="-previous">
                            <button className="-btn" disabled={!table.getCanPreviousPage()}
                                onClick={() => table.previousPage()}>
                                Anterior
                            </button>
                        </div>
                        <div className="-center">
                            <span className="-pageInfo">
                                Página
                                <div className="-pageJump px-2">
                                    <input aria-label="Ir para página" type="number"
                                        value={table.getState().pagination.pageIndex + 1}
                                        onChange={(event) => table.setPageIndex(event.target.valueAsNumber - 1)} />

                                </div>
                                de
                                <span className="-totalPages ps-2">{table.getPageCount()}</span>
                            </span>
                            <span className="select-wrap -pageSizeOptions">
                                <select aria-label="por página"
                                    value={table.getState().pagination.pageSize}
                                    onChange={e => table.setPageSize(e.target.value)}>
                                    <option value={5}>5 linhas</option>
                                    <option value={10}>10 linhas</option>
                                    <option value={20}>20 linhas</option>
                                    <option value={25}>25 linhas</option>
                                    <option value={50}>50 linhas</option>
                                    <option value={100}>100 linhas</option>
                                </select>
                            </span>
                        </div>
                        <div className="-next">
                            <button className="-btn" disabled={!table.getCanNextPage()}
                                onClick={() => table.nextPage()}
                            >
                                Próximo
                            </button>
                        </div>
                    </div>
                </div>
                <div className={`-loading  ${props.loading ? '-active' : ''}`}>
                    <div className="-loading-inner">Carregando...</div>
                </div>
            </div>
        </>
    );
}
